/**
 * Combat zones in the player's system (M5 spec §8.3, §14.3, §18 flow 3; plan C15).
 *
 * - **Zones** come from the manifest (`LocalManifest.zones`, on entering) and the 1 Hz system view
 *   (`SystemEconomyView.zones`: zones opening and closing while the player is here). A zone is its node's position
 *   (float64 world metres), `radiusM` around it (charter-conflict.json), and its two sides. Zone agents are
 *   ordinary local agents (traffic materializes them at the node); while a zone runs its two sides' teams are
 *   **mutually hostile** (`setHostile`; restored when the zone ends, unless they were hostile before).
 * - **Support:** a `deaths`-stage reader of the combat ring takes the player's hits (`hitHull`, `hitShield`) on a
 *   side's ships inside the zone and turns them into strategic strength for the **other** side (the damage as a
 *   fraction of the victim's hull × its hull's strength, the unit `ZoneView.strengthA/B` uses). The sums go out as
 *   `zoneSupport { zoneId, side, damage }` every `syncS` s (local.json: the traffic sync period). No crime.
 * - **HUD source** (C18): `nearestZone` and `zoneAt(i)` give the zones, their sides, centre and radius, and the
 *   prompt "Combat zone: Meridian vs Tethys. Attack one side to support the other." within `hudRangeM`.
 *   `zoneContaining` answers C14's crime exemption.
 * - **The conflict runtime** (`createCharterConflict`) binds all of the local conflict to the strategic client:
 *   zones, the blockade picture (`blockade.ts`) and the live toll policy (`tolls.ts`). The Charter subsystem
 *   (C14's `subsystem.ts`, order 460) calls `step(sim)` each tick and `leave()` on leaving the system.
 *
 * Per tick allocation-free (a pooled zone table, index loops, the ring cursor); applying zones and sending the
 * support batch are message-rate work.
 */
import { z } from 'zod'
import conflictJson from '../data/charter-conflict.json'
import factionsJson from '../data/strategic/factions.json'
import hullsJson from '../data/strategic/hulls.json'
import localJson from '../data/strategic/local.json'
import zonesJson from '../data/strategic/zones.json'
import { EV, eventSlot, firstReadable } from '../combat/events'
import { isHostile, setHostile } from '../combat/hostility'
import { logger } from '../core/logger'
import { registerSimStage, type Sim, type SimStageFn } from '../sim'
import type { StrategicClient } from '../strategic/client'
import type { FactionId, LocalManifest, NodeRef, StrategicCommand, SystemEconomyView } from '../strategic/types'
import { Agent } from '../traffic/components'
import type { ContactStore } from '../traffic/contacts'
import { Ship } from '../world/components'
import { factionShort, installLiveTollPolicy, type TollClient } from './tolls'
import { applyBlockadeManifest, applyBlockadeView, clearBlockade, liveBlockade, type BlockadeState } from './blockade'

const zonesConfigSchema = z.object({ radiusM: z.number().positive() }).strict()

/** Zone tuning: the fight radius (charter-conflict.json), the HUD range (zones.json), the batch period (local.json). */
export const ZONES_CONFIG = {
  ...zonesConfigSchema.parse(conflictJson.zones),
  hudRangeM: z.number().positive().parse(zonesJson.hudRangeM),
  batchS: z.number().positive().parse(localJson.syncS),
}

/** Local zones at once (a system rarely has more than one or two). */
export const LOCAL_ZONE_CAPACITY = 8

/** Faction id → its combat team (factions.json `teamId`). */
const TEAM_OF: ReadonlyMap<string, number> = new Map(factionsJson.factions.map((f) => [f.id, f.teamId]))
/** Hull id → its strategic strength (hulls.json). */
const HULL_STRENGTH: ReadonlyMap<string, number> = new Map(hullsJson.hulls.map((h) => [h.id, h.strength]))

/** A zone as the local layer keeps it (a pooled record). */
export interface LocalZoneState {
  id: string
  sideA: FactionId
  sideB: FactionId
  teamA: number
  teamB: number
  /** The node's float64 world position. */
  x: number
  y: number
  z: number
  radiusM: number
  /** Reach time the zone ends. */
  until: number
  /** "Combat zone: Meridian vs Tethys. Attack one side to support the other." */
  prompt: string
  /** Support not yet sent, per supported side. */
  pendingA: number
  pendingB: number
  /** True when this zone turned the two teams hostile (restored at its end). */
  madeHostile: boolean
}

export interface ZoneRuntime {
  readonly zones: LocalZoneState[]
  count: number
  /** Seconds since the last support batch. */
  elapsed: number
  /** The combat ring cursor of the support reader. */
  cursor: number
}

function blankZone(): LocalZoneState {
  return { id: '', sideA: 'mutual', sideB: 'mutual', teamA: -1, teamB: -1, x: 0, y: 0, z: 0, radiusM: 0, until: 0, prompt: '', pendingA: 0, pendingB: 0, madeHostile: false }
}

export function createZoneRuntime(): ZoneRuntime {
  const zones: LocalZoneState[] = []
  for (let i = 0; i < LOCAL_ZONE_CAPACITY; i++) zones.push(blankZone())
  return { zones, count: 0, elapsed: 0, cursor: 0 }
}

/** The HUD prompt of a zone between `a` and `b`. */
export function zonePrompt(a: FactionId, b: FactionId): string {
  return `Combat zone: ${factionShort(a)} vs ${factionShort(b)}. Attack one side to support the other.`
}

/** What a zone input needs (both `LocalZone` and `ZoneView` have it). */
export interface ZoneInput {
  id: string
  sideA: FactionId
  sideB: FactionId
  node: NodeRef
  until: number
}

/** Ends zone `k` (restores the hostility it set) and removes it (swap-remove). Pending support is dropped. */
function endZone(zr: ZoneRuntime, k: number): void {
  const zs = zr.zones[k]
  if (zs.madeHostile) setHostile(zs.teamA, zs.teamB, false)
  const last = zr.count - 1
  if (k !== last) {
    zr.zones[k] = zr.zones[last]
    zr.zones[last] = zs
  }
  zs.id = ''
  zs.madeHostile = false
  zr.count = last
}

/**
 * Makes the local zones `list` (message rate): zones no longer listed end (their unsent support goes out first
 * through `send`), new ones start with their sides mutually hostile, known ones refresh their end time.
 */
export function applyZones(zr: ZoneRuntime, list: readonly ZoneInput[], send: (cmd: StrategicCommand) => void): void {
  for (let k = zr.count - 1; k >= 0; k--) {
    const id = zr.zones[k].id
    if (list.some((zv) => zv.id === id)) continue
    flushZone(zr.zones[k], send)
    endZone(zr, k)
  }
  for (const zv of list) {
    let k = -1
    for (let i = 0; i < zr.count; i++) if (zr.zones[i].id === zv.id) k = i
    if (k >= 0) {
      zr.zones[k].until = zv.until
      continue
    }
    if (zr.count >= LOCAL_ZONE_CAPACITY) {
      logger.warn(`charter zones: more than ${LOCAL_ZONE_CAPACITY} zones in the system; ${zv.id} is not shown`)
      continue
    }
    const zs = zr.zones[zr.count++]
    zs.id = zv.id
    zs.sideA = zv.sideA
    zs.sideB = zv.sideB
    zs.teamA = TEAM_OF.get(zv.sideA) ?? -1
    zs.teamB = TEAM_OF.get(zv.sideB) ?? -1
    zs.x = zv.node.pos[0]
    zs.y = zv.node.pos[1]
    zs.z = zv.node.pos[2]
    zs.radiusM = ZONES_CONFIG.radiusM
    zs.until = zv.until
    zs.prompt = zonePrompt(zv.sideA, zv.sideB)
    zs.pendingA = 0
    zs.pendingB = 0
    zs.madeHostile = zs.teamA >= 0 && zs.teamB >= 0 && !isHostile(zs.teamA, zs.teamB)
    if (zs.madeHostile) setHostile(zs.teamA, zs.teamB, true)
  }
}

/** Ends every zone (the system was left). Unsent support is dropped (the core resolves the zone off-screen). */
export function clearZones(zr: ZoneRuntime): void {
  for (let k = zr.count - 1; k >= 0; k--) endZone(zr, k)
  zr.elapsed = 0
}

/** The zone whose sphere holds the point, or −1. Allocation-free. */
export function zoneContaining(zr: ZoneRuntime, x: number, y: number, z: number): number {
  for (let k = 0; k < zr.count; k++) {
    const zs = zr.zones[k]
    const dx = x - zs.x
    const dy = y - zs.y
    const dz = z - zs.z
    if (dx * dx + dy * dy + dz * dz <= zs.radiusM * zs.radiusM) return k
  }
  return -1
}

/** The nearest zone within `rangeM` (default: the HUD range) of the point, or −1. Allocation-free. */
export function nearestZone(zr: ZoneRuntime, x: number, y: number, z: number, rangeM: number = ZONES_CONFIG.hudRangeM): number {
  let best = -1
  let bestD2 = rangeM * rangeM
  for (let k = 0; k < zr.count; k++) {
    const zs = zr.zones[k]
    const dx = x - zs.x
    const dy = y - zs.y
    const dz = z - zs.z
    const d2 = dx * dx + dy * dy + dz * dz
    if (d2 <= bestD2) {
      bestD2 = d2
      best = k
    }
  }
  return best
}

/**
 * Adds the player's `damage` hull points on a ship of `faction` (hull `hullId`, `maxHull` points) at the point to
 * the zone there, as support for the other side. Returns the zone index or −1 (not a side, or outside every zone).
 */
export function addPlayerDamage(zr: ZoneRuntime, faction: FactionId, hullId: string, damage: number, maxHull: number, x: number, y: number, z: number): number {
  if (!(damage > 0) || !(maxHull > 0)) return -1
  for (let k = 0; k < zr.count; k++) {
    const zs = zr.zones[k]
    const onA = zs.sideA === faction
    if (!onA && zs.sideB !== faction) continue
    const dx = x - zs.x
    const dy = y - zs.y
    const dz = z - zs.z
    if (dx * dx + dy * dy + dz * dz > zs.radiusM * zs.radiusM) continue
    const d = (damage / maxHull) * (HULL_STRENGTH.get(hullId) ?? 1)
    if (onA) zs.pendingB += d
    else zs.pendingA += d
    return k
  }
  return -1
}

/** Sends a zone's unsent support (message rate). */
function flushZone(zs: LocalZoneState, send: (cmd: StrategicCommand) => void): void {
  if (zs.pendingA > 0) send({ type: 'zoneSupport', zoneId: zs.id, side: 'a', damage: zs.pendingA })
  if (zs.pendingB > 0) send({ type: 'zoneSupport', zoneId: zs.id, side: 'b', damage: zs.pendingB })
  zs.pendingA = 0
  zs.pendingB = 0
}

/** Advances the batch timer by `dt`; every `batchS` the pending support goes out. Returns true when it flushed. */
export function stepZoneBatch(zr: ZoneRuntime, dt: number, send: (cmd: StrategicCommand) => void, batchS: number = ZONES_CONFIG.batchS): boolean {
  zr.elapsed += dt
  if (zr.elapsed < batchS) return false
  zr.elapsed = 0
  for (let k = 0; k < zr.count; k++) flushZone(zr.zones[k], send)
  return true
}

/**
 * Reads the combat ring since the cursor: each hit by the player on a tracked local agent of a zone side inside
 * the zone becomes support (`addPlayerDamage`). Allocation-free.
 */
export function readZoneHits(zr: ZoneRuntime, sim: Sim, store: ContactStore): void {
  const ring = sim.events
  const player = sim.playerEid
  for (let i = firstReadable(ring, zr.cursor); i < ring.head; i++) {
    const e = eventSlot(i)
    const type = ring.type[e]
    if (type !== EV.hitHull && type !== EV.hitShield) continue
    if (player < 0 || ring.other[e] !== player) continue
    const victim = ring.eid[e]
    if (victim < 0) continue
    const slot = Agent.index[victim]
    if (!(slot >= 0 && slot < store.count && store.eid[slot] === victim)) continue
    const def = sim.defs[Ship.defIndex[victim]]
    if (def === undefined) continue
    addPlayerDamage(zr, store.faction[slot], store.hull[slot], ring.amount[e], def.hull, ring.x[e], ring.y[e], ring.z[e])
  }
  zr.cursor = ring.head
}

// ---------------------------------------------------------------------------------------------
// The conflict runtime (zones + blockade + tolls over the strategic client)
// ---------------------------------------------------------------------------------------------

/** The client calls the local conflict makes. */
export type ConflictClient = Pick<StrategicClient, 'onManifest' | 'onSystemView' | 'command' | 'manifest'> & TollClient

export interface CharterConflictDeps {
  /** The strategic client, or null before the Reach's economy runs (checked each step until bound). */
  client(): ConflictClient | null
  /** The traffic store (the local agents), or null. */
  store(): ContactStore | null
  /** The seconds one step covers (the sim's fixed step). */
  readonly dt: number
}

export interface CharterConflict {
  readonly zones: ZoneRuntime
  readonly blockade: BlockadeState
  /** One tick: binds the client once, applies queued zones, reads the player's hits and batches the support. */
  step(sim: Sim): void
  /** The system was left: zones end, the blockade clears. */
  leave(): void
  /** Unbinds everything (tests, teardown). */
  dispose(): void
}

function logSendError(err: unknown): void {
  logger.warn('charter zones: a zone report failed', err)
}

export function createCharterConflict(deps: CharterConflictDeps): CharterConflict {
  const zr = createZoneRuntime()
  const blockade = liveBlockade
  let client: ConflictClient | null = null
  let systemId: string | null = null
  let pending: readonly ZoneInput[] | null = null
  let stagedSim: Sim | null = null
  let unstage: (() => void) | null = null
  const offs: (() => void)[] = []
  const send = (cmd: StrategicCommand): void => {
    client?.command(cmd).catch(logSendError)
  }
  const onManifest = (m: LocalManifest): void => {
    systemId = m.systemId
    pending = m.zones
    applyBlockadeManifest(blockade, m)
  }
  const onView = (v: SystemEconomyView): void => {
    if (v.systemId !== systemId) return
    pending = v.zones
    applyBlockadeView(blockade, v)
  }
  const reader: SimStageFn = (sim) => {
    const store = deps.store()
    if (store !== null && zr.count > 0) readZoneHits(zr, sim, store)
    else zr.cursor = sim.events.head
  }
  function bind(c: ConflictClient): void {
    client = c
    offs.push(c.onManifest(onManifest), c.onSystemView(onView), installLiveTollPolicy(c))
    if (c.manifest !== null) onManifest(c.manifest)
  }
  return {
    zones: zr,
    blockade,
    step(sim: Sim): void {
      if (client === null) {
        const c = deps.client()
        if (c === null) return
        bind(c)
      }
      blockade.store = deps.store()
      if (stagedSim !== sim) {
        unstage?.()
        stagedSim = sim
        zr.cursor = sim.events.head
        unstage = registerSimStage(sim, 'deaths', reader)
      }
      if (pending !== null) {
        const list = pending
        pending = null
        applyZones(zr, list, send)
      }
      if (zr.count > 0) stepZoneBatch(zr, deps.dt, send)
    },
    leave(): void {
      clearZones(zr)
      clearBlockade(blockade)
      systemId = null
      pending = null
    },
    dispose(): void {
      clearZones(zr)
      clearBlockade(blockade)
      unstage?.()
      unstage = null
      stagedSim = null
      for (const off of offs) off()
      offs.length = 0
      client = null
    },
  }
}
